/**
 * Persoenliche Bestleistungen und Lebensleistung im Browser-Speicher.
 *
 * Bewusst getrennt von der Bestenliste auf dem Server: Die App muss ohne Netz
 * und ohne Namen vollstaendig spielbar sein. Der Server ist die Kuer, nicht die
 * Voraussetzung.
 */

import type { Discipline, RunResult } from './disciplines'

const RECORDS_KEY = 'som.records.v1'
const PROFILE_KEY = 'som.profile.v1'

export interface PersonalRecord {
  distanceCm: number
  timeMs: number
  /** Zeitstempel in Millisekunden seit 1970. */
  achievedAt: number
}

export interface RecordStore {
  /** Schluessel ist `discipline` bzw. `discipline:variant`. */
  best: Record<string, PersonalRecord>
  /** Summe aller je gescrollten Zentimeter. */
  lifetimeCm: number
  /** Anzahl abgeschlossener Versuche. */
  attempts: number
}

export interface Profile {
  /** Anzeigename fuer die Bestenliste; leer, solange nicht gesetzt. */
  name: string
  /** Zufaellige, geraetegebundene Kennung. Kein Login, nur Wiedererkennung. */
  clientId: string
}

const EMPTY_STORE: RecordStore = { best: {}, lifetimeCm: 0, attempts: 0 }

export function recordKey(discipline: Discipline, variant: string | null): string {
  return variant ? `${discipline}:${variant}` : discipline
}

/**
 * Beim Sprint ist die kuerzere Zeit besser, sonst die groessere Strecke.
 * Diese eine Funktion entscheidet ueberall in der App, was "besser" heisst.
 */
export function isBetter(
  discipline: Discipline,
  candidate: { distanceCm: number; timeMs: number },
  existing: { distanceCm: number; timeMs: number } | undefined,
): boolean {
  if (!existing) return true
  if (discipline === 'sprint') return candidate.timeMs < existing.timeMs
  return candidate.distanceCm > existing.distanceCm
}

export function loadRecords(): RecordStore {
  const raw = readJson<RecordStore>(RECORDS_KEY)
  if (!raw) return { ...EMPTY_STORE, best: {} }
  return {
    best: raw.best ?? {},
    lifetimeCm: Number(raw.lifetimeCm) || 0,
    attempts: Number(raw.attempts) || 0,
  }
}

/**
 * Verbucht einen Versuch und meldet zurueck, ob es ein neuer Rekord war -
 * die Oberflaeche braucht das fuer den Jubel.
 */
export function commitRun(result: RunResult): { store: RecordStore; isRecord: boolean } {
  const store = loadRecords()
  const key = recordKey(result.discipline, result.variant)
  const previous = store.best[key]
  const better = isBetter(result.discipline, result, previous)

  if (better) {
    store.best[key] = {
      distanceCm: result.distanceCm,
      timeMs: result.timeMs,
      achievedAt: Date.now(),
    }
  }
  store.lifetimeCm += result.distanceCm
  store.attempts += 1
  writeJson(RECORDS_KEY, store)
  return { store, isRecord: better }
}

/**
 * Langstrecke laeuft ohne festes Ende. Damit die Lebensleistung mitwaechst,
 * ohne bei jedem Zentimeter zu schreiben, bucht der Screen in Abstaenden nur
 * den Zuwachs nach.
 */
export function addLifetime(deltaCm: number): void {
  if (deltaCm <= 0) return
  const store = loadRecords()
  store.lifetimeCm += deltaCm
  writeJson(RECORDS_KEY, store)
}

export function loadProfile(): Profile {
  const raw = readJson<Profile>(PROFILE_KEY)
  if (raw?.clientId) return { name: raw.name ?? '', clientId: raw.clientId }
  const profile: Profile = { name: '', clientId: createClientId() }
  writeJson(PROFILE_KEY, profile)
  return profile
}

export function saveProfile(profile: Profile): void {
  writeJson(PROFILE_KEY, profile)
}

function createClientId(): string {
  // Absteigend nach Qualitaet, weil aeltere WebViews die bequeme Variante nicht
  // kennen. Die Kennung dient nur der Wiedererkennung in der Bestenliste - sie
  // muss unverwechselbar sein, aber nichts geheim halten.
  const api: Crypto | undefined = globalThis.crypto
  if (typeof api?.randomUUID === 'function') return api.randomUUID()

  if (typeof api?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16)
    api.getRandomValues(bytes)
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  }

  return `alt-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    // Privater Modus oder beschaedigter Eintrag. Die App laeuft ohne weiter.
    return null
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Speicher voll oder gesperrt - Bestleistungen gehen verloren, das Spiel
    // laeuft weiter. Kein Abbruch fuer etwas Nebensaechliches.
  }
}
